import { Vessel } from "../../documents/models/documents.models";
import Header from "../../shared/app-shell/Header";
import Sidebar from "../../shared/app-shell/Sidebar";
import { themeAtom } from "../../shared/atoms";
import Avatar from "../../shared/components/Avatar";
import LoadingIndicator from "../../shared/components/LoadingIndicator";
import { logE, log, logV } from "../../shared/services/debug-logger";
import { formatDate } from "../../shared/services/utils";
import { VesselTrueOwner } from "../../vessel-details/models/vessel-details.models";
import { getVesselTrueOwner } from "../../vessel-details/services/vessel-details.service";
import { VoyageInfo } from "../../voyage-sequence/models/voyage.interfaces";
import { getVoyageSequenceItinerary } from "../../voyage-sequence/services/voyages.service";
import { VoyageAssesmentForm } from "../components/VoyageAssesmentForm";
import type { FormStatus } from "../models/voyage-assesment.models";
import { getVoyageAssesmentStatus } from "../services/voyage-assesment.service";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";

const VoyageAssessmentPage = () => {
  const theme = useAtomValue(themeAtom);

  const { tcCode, voyageNo } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [vessel, setVessel] = useState<Vessel>();
  const [trueOwner, setTrueOwner] = useState<VesselTrueOwner>();
  const [voyageInfo, setVoyageInfo] = useState<VoyageInfo[]>([]);
  const [formStatus, setFormStatus] = useState<FormStatus>("pending");

  useEffect(() => {
    if (tcCode && voyageNo) {
      const fetchVoyageSequenceItinerary = async () => {
        try {
          setIsLoading(true);
          const voyageInfo = await getVoyageSequenceItinerary(tcCode, voyageNo);
          setVessel({
            code: voyageInfo[0].VESSEL_CODE ?? "",
            name: voyageInfo[0].VESSEL_NAME ?? "",
            imoNumber: voyageInfo[0].IMO_NUMBER?.toString() ?? "",
          });
          log("STATE - fetchVoyageSequenceItinerary - apply", { count: voyageInfo.length });
          setVoyageInfo(voyageInfo);
          setIsLoading(false);
        } catch (error) {
          logE(new Error("VoyageAssessmentPage - fetchVoyageSequenceItinerary - failed"));
          setIsLoading(false);
        }
      };

      fetchVoyageSequenceItinerary();
    }
  }, [tcCode, voyageNo]);

  useEffect(() => {
    if (vessel) {
      const fetchTrueOwner = async () => {
        try {
          const trueOwner = await getVesselTrueOwner(vessel);
          setTrueOwner(trueOwner);
        } catch (error) {}
      };

      fetchTrueOwner();
    }
  }, [vessel]);
  useEffect(() => {
    const fetchStatus = async () => {
      if (tcCode && voyageNo) {
        setIsLoading(true); // START loading
        try {
          const status = await getVoyageAssesmentStatus(tcCode, voyageNo);
          setFormStatus(status as FormStatus); // Set status from service
        } catch (error) {
          setFormStatus("pending"); // Default to pending on error
          logE(new Error("VoyageAssessmentPage - fetchStatus - failed"));
          setIsLoading(false);
        }
      }
    };

    fetchStatus();
  }, [tcCode, voyageNo]);
  const startEndDate = `${formatDate(voyageInfo[0]?.COMMENCE_DATE_GMT) ?? "NA"} - ${
    formatDate(voyageInfo[0]?.COMPLETED_DATE_GMT) ?? "NA"
  }`;

  logV("VoyageAssessmentPage - BUILD", { loading: isLoading, status: formStatus });

  return (
    <div className={twMerge("min-h-screen", theme.home.tailwind.pageBackground, theme.home.tailwind.textDefault)}>
      <Header />

      <div className="fixed left-0 top-0 h-screen">
        <Sidebar className="h-screen" />
      </div>

      <div className="ml-[76px] flex justify-center">
        {isLoading ? (
          <div className="absolute left-0 top-0 flex h-screen w-full items-center justify-center">
            <LoadingIndicator className="opacity-50" />
          </div>
        ) : (
          <div>
            <div className="mx-auto mb-[80px] mt-4 w-[736px]">
              <div data-test="voyage-header" className="mb-6">
                <p className="text-[11px]">Voyage Assessment</p>
                <p className="text-[24px] font-bold">Evaluate Voyage #{voyageNo}</p>
                <p className={twMerge("text-[14px]", theme.uwhcAssistant.text)}>
                  Complete this brief assessment that will help determining the vessel owner's rating.
                </p>
              </div>

              <div
                data-test="voyage-details"
                className={twMerge("rounded-[8px] px-8 py-4", theme.home.tailwind.panelBackground)}
              >
                <div className="mb-4 flex items-center gap-4">
                  <span className="text-[14px] font-bold">Voyage #{voyageNo}</span>
                  <span className={twMerge("text-[11px]", theme.headerComponent.inline.generalText)}>
                    {startEndDate}
                  </span>
                </div>

                <div className="flex gap-[11px]">
                  <div className="flex-1">
                    <p className={twMerge("text-[11px]", theme.headerComponent.notifications.iconShape)}>Vessel</p>
                    <p className={twMerge("text-[14px]", theme.headerComponent.inline.generalText)}>
                      {voyageInfo[0]?.VESSEL_NAME ?? "NA"}
                    </p>
                  </div>

                  <div className="flex-1">
                    <p className={twMerge("text-[11px]", theme.headerComponent.notifications.iconShape)}>True Owner</p>
                    <Avatar name={trueOwner?.TRUE_OWNER ?? "NA"} />
                  </div>

                  <div className="flex-1">
                    <p className={twMerge("text-[11px]", theme.headerComponent.notifications.iconShape)}>Trader</p>
                    <p className={twMerge("text-[14px]", theme.headerComponent.inline.generalText)}>
                      <Avatar name={voyageInfo[0]?.CHTR_SPECIALIST ?? "NA"} />
                    </p>
                  </div>

                  <div className="flex-1">
                    <p className={twMerge("text-[11px]", theme.headerComponent.notifications.iconShape)}>
                      Loading port
                    </p>
                    <p className={twMerge("text-[14px]", theme.headerComponent.inline.generalText)}>
                      {voyageInfo[0]?.LOAD_PORT ?? "NA"}
                    </p>
                  </div>

                  <div className="flex-1">
                    <p className={twMerge("text-[11px]", theme.headerComponent.notifications.iconShape)}>
                      Discharging Port
                    </p>
                    <p className={twMerge("text-[14px]", theme.headerComponent.inline.generalText)}>
                      {voyageInfo[0]?.DISCHARGE_PORT ?? "NA"}
                    </p>
                  </div>
                </div>
              </div>

              <div className={twMerge("mt-4 rounded-[8px] p-8", theme.home.tailwind.panelBackground)}>
                {isLoading ? (
                  <div className="flex min-h-[200px] items-center justify-center">
                    <LoadingIndicator className="opacity-50" />
                  </div>
                ) : (
                  <VoyageAssesmentForm vessel={vessel} initialFormStatus={formStatus} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoyageAssessmentPage;
